"use client"
import { useEffect, useState, useRef } from "react"
import { motion } from "framer-motion"
import styles from "./SignupSuccessOverlay.module.css"
import { actionCreateSignup } from "@/app/api/signUp/create/actions"
import type { TApiSignupCreateParams, TApiSignupCreateResponse } from "@/api/launcherApp/signup/schema"
import type { TEnrichedPersonData } from "@/app/api/enrich/email/schema"
import { makeURL } from "@nauverse/make-url"
import { config } from "@/config"
import { LAUNCHER_APP_PATH } from "@/config/defaults"
import { Modal, PasswordInput, TextInput } from "@mantine/core"
import { deleteCookie } from "cookies-next/client"
import clsx from "clsx"
import { usePostHog } from "posthog-js/react"

const PHRASES = [
  "Incepting your account",
  "Configuring awesomeness",
  "Cracking knuckles",
  "Getting ready for WOW",
  "Crunching quota",
  "Waking up Joe Test",
  "Remembering Glengarry",
  "Getting in the zone",
  "Resetting the hustlemeter",
  "Caffeinating the server",
  "Firing up the droids",
  "Counting down to done",
  "Reticulating splines",
  "Calling the banners",
  "Booting up the bots",
  "Haranguing the troops",
  "Pregaming action",
] as const

// Number of visible items at once
const VISIBLE_ITEMS = 5
// Position of the active item (0-indexed)
const ACTIVE_ITEM_POSITION = 2
// Time between advancing items (in milliseconds)
const ITEM_DURATION = 2000
// Height of each item (in pixels)
const ITEM_HEIGHT = 50

function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

interface CheckmarkSVGProps {
  isDrawing: boolean
  isComplete: boolean
}

function CheckmarkSVG({ isDrawing, isComplete }: CheckmarkSVGProps) {
  const pathVariants = {
    hidden: { pathLength: 0 },
    visible: { pathLength: 1 },
    predrawn: { pathLength: 1 },
  }

  return (
    <g>
      <motion.circle
        cx={12}
        cy={12}
        r={11}
        stroke="white"
        strokeWidth={2}
        fill="transparent"
        variants={pathVariants}
        initial={isComplete ? "predrawn" : "hidden"}
        animate="visible"
        transition={{
          duration: isComplete ? 0 : 0.4,
          ease: "easeOut",
        }}
      />
      <motion.path
        d="M7.5 12.5L10.5 15.5L16.5 9.5"
        stroke="white"
        strokeWidth={2}
        fill="transparent"
        variants={pathVariants}
        initial={isComplete ? "predrawn" : "hidden"}
        animate="visible"
        transition={{
          duration: isComplete ? 0 : 0.3,
          delay: isComplete ? 0 : 0.4,
          ease: "easeOut",
        }}
      />
    </g>
  )
}

interface PhraseItemProps {
  phrase: string
  visualPosition: number
  isActive: boolean
  isComplete: boolean
  progress: number
}

function PhraseItem({ phrase, visualPosition, isActive, isComplete, progress }: PhraseItemProps) {
  // Calculate the y position with smooth transition using progress (0-1)
  const y = (visualPosition - progress) * ITEM_HEIGHT

  // Calculate opacity based on distance from center
  const distanceFromCenter = Math.abs(visualPosition - progress - ACTIVE_ITEM_POSITION)
  const opacity = distanceFromCenter >= 2 ? 0 : distanceFromCenter <= 0.5 ? 1 : 1 - (distanceFromCenter - 0.5) * 0.7

  return (
    <motion.div
      className={clsx(styles.phraseContainer, "prevent-select")}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        opacity,
        y,
        transition: "opacity 0.3s ease",
      }}
    >
      <span className={styles.text}>{phrase}</span>
      {(isActive || isComplete) && (
        <motion.svg
          width="24"
          height="24"
          style={{
            position: "absolute",
            left: 10,
          }}
        >
          <CheckmarkSVG isDrawing={isActive} isComplete={isComplete} />
        </motion.svg>
      )}
    </motion.div>
  )
}

const loginURL = makeURL(config.environment.api.launcherURL ?? config.environment.api.baseURL, `${LAUNCHER_APP_PATH}/login/web`, {
  config: {
    trailingSlash: "remove",
  },
  params: {
    resource: "next",
  },
})

export function SignupSuccessOverlay({
  onSuccess,
  onError,
  createParams,
  userData,
  enrichmentData,
  opened,
  onClose,
}: {
  onSuccess?: () => void
  onError?: () => void
  createParams?: TApiSignupCreateParams
  userData?: {
    email: string
    password: string
    plan: "solo" | "team"
    companySize?: string
    companyReps?: string
    companyCountry?: string
  }
  enrichmentData?: TEnrichedPersonData | null
  opened: boolean
  onClose: () => void
}) {
  const posthog = usePostHog()
  const isCreating = useRef(false)
  const formRef = useRef<HTMLFormElement>(null)

  // Shuffle and prepare phrases
  const shuffledPhrases = useRef(shuffleArray([...PHRASES]))

  // Current progress (0-1) for smooth animation
  const [progress, setProgress] = useState(0)

  // Current start index in the phrases array
  const [startIndex, setStartIndex] = useState(0)

  // Animation timing references
  const startTimeRef = useRef<number | null>(null)
  const animationRef = useRef<number>(0)
  const progressRef = useRef(0)

  // Create a window of phrases to display
  const visiblePhrases = Array.from({ length: VISIBLE_ITEMS + 2 }, (_, i) => {
    const phraseIndex = (startIndex + i) % shuffledPhrases.current.length
    return {
      id: `${phraseIndex}-${startIndex}`, // Unique key combining phrase index and start index
      phrase: shuffledPhrases.current[phraseIndex],
      visualPosition: i,
      isActive: i === ACTIVE_ITEM_POSITION + 1 && progress < 0.5,
      isComplete: i < ACTIVE_ITEM_POSITION + 1 || (i === ACTIVE_ITEM_POSITION + 1 && progress >= 0.5),
    }
  })

  useEffect(() => {
    // Use a stable, consistent timestamp reference
    const startAnimation = () => {
      startTimeRef.current = performance.now()
      progressRef.current = 0

      const animate = () => {
        // Get current timestamp for precise timing
        const now = performance.now()
        const elapsedSinceStart = startTimeRef.current !== null ? now - startTimeRef.current : 0

        // Calculate progress based on elapsed time and duration
        const newProgress = (elapsedSinceStart % ITEM_DURATION) / ITEM_DURATION

        // Check if we've completed a full item cycle
        const completedCycles = Math.floor(elapsedSinceStart / ITEM_DURATION)

        // Update start index if needed
        if (completedCycles > 0 && progressRef.current < 1 && newProgress >= 0 && newProgress < progressRef.current) {
          setStartIndex((prevIndex) => (prevIndex + 1) % shuffledPhrases.current.length)
        }

        // Update progress state only when it changes significantly enough
        // This reduces unnecessary re-renders
        if (Math.abs(newProgress - progressRef.current) > 0.003) {
          setProgress(newProgress)
          progressRef.current = newProgress
        }

        animationRef.current = requestAnimationFrame(animate)
      }

      animate()
    }

    startAnimation()

    return () => {
      cancelAnimationFrame(animationRef.current)
    }
  }, [])

  useEffect(() => {
    if (isCreating.current) {
      return
    }

    if (!createParams) {
      return
    }

    isCreating.current = true

    async function createSignup() {
      try {
        if (!createParams) {
          throw new Error("No create params")
        }

        let succeededFirstTime = false
        let createResult: { success: boolean; data?: TApiSignupCreateResponse } | undefined

        try {
          createResult = await actionCreateSignup(createParams)
          succeededFirstTime = true
        } catch (err) {
          //
        }

        if (!succeededFirstTime) {
          createResult = await actionCreateSignup(createParams)
        }

        if (!createResult || !createResult.success || !createResult.data?.success) {
          throw new Error("Failed to create signup")
        }

        if (!formRef.current) {
          throw new Error("No form ref")
        }

        if (!userData) {
          throw new Error("No user data")
        }

        // Delete it if it exists
        deleteCookie("ringio-signed-up", {
          httpOnly: false,
          secure: true,
          sameSite: "lax",
          domain: ".ring.io",
        })

        // Set user properties on PostHog (will be linked to Ringio user on login)
        posthog.identify(userData.email, {
          email: userData.email,
          plan: userData.plan,
          ...(userData.companySize && { companySize: userData.companySize }),
          ...(userData.companyReps && { companyReps: userData.companyReps }),
          ...(userData.companyCountry && { companyCountry: userData.companyCountry }),
          // Apollo enrichment data as user properties
          ...(enrichmentData && {
            enriched: true,
            enrichedTitle: enrichmentData.title,
            enrichedIndustry: enrichmentData.apolloData?.industry,
            enrichedEmployeeCount: enrichmentData.apolloData?.employeeCount,
            enrichedRevenue: enrichmentData.apolloData?.revenue,
            enrichedTechnologies: enrichmentData.apolloData?.technologies,
            enrichedLinkedin: enrichmentData.apolloData?.linkedinUrl,
          }),
        })

        posthog.capture("user_signed_up", {
          email: userData.email,
          plan: userData.plan,
          ...(userData.companySize && { companySize: userData.companySize }),
          ...(userData.companyReps && { companyReps: userData.companyReps }),
          ...(userData.companyCountry && { companyCountry: userData.companyCountry }),
          // Apollo enrichment data
          ...(enrichmentData && {
            enriched: true,
            enrichedTitle: enrichmentData.title,
            enrichedIndustry: enrichmentData.apolloData?.industry,
            enrichedEmployeeCount: enrichmentData.apolloData?.employeeCount,
            enrichedRevenue: enrichmentData.apolloData?.revenue,
            enrichedTechnologies: enrichmentData.apolloData?.technologies,
            enrichedLinkedin: !!enrichmentData.apolloData?.linkedinUrl,
          }),
        })

        // formRef.current.submit()
        // onSuccess?.()
      } catch (err) {
        onError?.()
      } finally {
        isCreating.current = false
      }
    }

    createSignup()
  }, [onSuccess, onError, createParams, userData, enrichmentData, posthog])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      radius={0}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      lockScroll={true}
      trapFocus={true}
      classNames={{
        root: styles.modal,
        body: styles.body,
      }}
    >
      <div className={styles.overlay}>
        <div
          className={styles.container}
          style={{
            position: "relative",
            height: `${ITEM_HEIGHT * VISIBLE_ITEMS}px`,
            overflow: "hidden",
          }}
        >
          {visiblePhrases.map((item) => (
            <PhraseItem key={item.id} phrase={item.phrase} visualPosition={item.visualPosition} isActive={item.isActive} isComplete={item.isComplete} progress={progress} />
          ))}
        </div>

        <div
          style={{
            display: "none",
          }}
        >
          <form ref={formRef} method="POST" action={loginURL}>
            <TextInput readOnly name={"email"} value={userData?.email} />
            <PasswordInput readOnly name={"password"} value={userData?.password} />
          </form>
        </div>
      </div>
    </Modal>
  )
}
